import '@/components/ag-grid/config/agGridModules';

import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { preloadLucideIcons, registerCustomIcons } from '@utilities-one/gpm-ui';

import { App } from '@/App';
import { initPageLifecycleWatchdog } from '@/library/diagnostics/pageLifecycle';

import '@/assets/css/globals.css';

const apiUrl = import.meta.env.VITE_API_URL;

const isTouchDevice = navigator.maxTouchPoints > 0;

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
  enabled: !!import.meta.env.VITE_SENTRY_DSN,
  integrations: [
    Sentry.browserTracingIntegration({
      // Map tiles arrive as resource.img spans — hundreds per map pageload, pure noise.
      ignoreResourceSpans: ['resource.img'],
      // Skip http.client spans for tile / COG / basemap fetches.
      shouldCreateSpanForRequest: (url) =>
        !url.includes('digitaloceanspaces.com') &&
        !url.includes('tiles.utilitiesone.com') &&
        !url.includes('arcgis.com'),
    }),
    ...(isTouchDevice ? [] : [Sentry.replayIntegration()]),
  ],
  tracePropagationTargets: apiUrl ? ['localhost', apiUrl] : ['localhost'],
  // 1% of traces — the sampled ones are still captured end-to-end (webapp + API).
  tracesSampleRate: 0.01,
  // Record a replay only when an error happens, not random sessions.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  sendDefaultPii: true,
  // Filter out third-party / browser-extension noise (e.g. injected views.js).
  ignoreErrors: ['updateFrom'],
});

initPageLifecycleWatchdog();

// Preload Lucide icons and register custom icons before first paint so `custom:*` icons render
// immediately. Icon loading is best-effort — a failed fetch must never keep the app from mounting.
async function bootstrap() {
  try {
    await Promise.all([preloadLucideIcons(), registerCustomIcons()]);
  } catch (error) {
    Sentry.captureException(error);
  }

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Failed to find the root element. Ensure index.html contains <div id="root"></div>');
  }

  ReactDOM.createRoot(rootElement).render(<App />);
}

void bootstrap();
